import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

const linkLesson0215 = getLinkForCourseLesson(
  "4eb31243-b6cb-423f-919d-15bba103f7ce",
  "cbfb80bf-b3d6-4634-b85c-c89eeec53cae"
);
const linkLesson0219 = getLinkForCourseLesson(
  "4eb31243-b6cb-423f-919d-15bba103f7ce",
  "f95203e7-0946-4a0b-ba9e-28ecb546405c"
);
const linkLesson0221 = getLinkForCourseLesson(
  "4eb31243-b6cb-423f-919d-15bba103f7ce",
  "910dc3d9-eb0b-4522-8485-9121b2724384"
);
const linkLesson0309 = getLinkForCourseLesson(
  "49817b85-0b30-4661-8a25-1e5176041865",
  "245e1400-a30e-4a82-ad58-b9067687fa9a"
);
const linkLesson0508 = getLinkForCourseLesson(
  "53e2fab0-edf6-4634-a20e-ae0cdfe73cba",
  "4494cf0e-f672-44de-8353-dbe4b03d37ef"
);
const linkLesson0509 = getLinkForCourseLesson(
  "53e2fab0-edf6-4634-a20e-ae0cdfe73cba",
  "d4b1522f-5eb0-46bd-87a9-d167c5f1c52e"
);

const theory = `<h3 class=sectionHead>What will you learn in this lesson?</h3>
<p>This lesson is an introduction to basic physical phenomena that occur when a propagating sound wave hits a surface and interacts with it. These are sound reflection, absorption, scattering and diffraction. Basic analytical expressions are introduced for calculating various acoustic parameters connected with these terms.
<h3 class=sectionHead>Introduction</h3>
<p>When we enter a room for the first time, we immediately become aware of what it looks like, but, at the same time, we also perceive how it sounds. This is especially true if there is already an active sound source in the room, or if we produce some sounds ourselves, such as footsteps, hand claps or even speech. We usually do not think too much about the acoustics of the room; we simply like it or dislike it on a subconscious level, depending on how much the acoustical conditions in the room agree with our expectations and previous experiences. This is connected with the term acoustic comfort, which is related to our subjective feeling of reverberance and the quantity of noise in the room. Moreover, the so-called acoustics of a room directly depends on its volume, shape and, arguably to the largest extent, to the acoustical characteristics of its surfaces (walls, floor, ceiling, furniture, etc.).<br class=newline>In this lesson, basic principles of reflection, absorption, diffusion, and diffraction of sound waves on the boundary surfaces of a room are explained. All these effects can be compared to waves in water, which are much more intuitive for us as we can see them, unlike the sound waves in air. We investigate these effects from the perspective of sound as a wave (so-called wave-based approach), since it would not be possible to explain these phenomena using the ray-based approach, i.e. if sound is considered to be (exclusively) rays of energy emitted from a sound source. For more information, please check the <a href="${linkLesson0309}" target="_blank">Lesson 3.9</a> on Ray-tracing.
<h3 class=sectionHead>Absorption and reflection</h3>
<p>Good speech intelligibility (<a href="${linkLesson0219}" target="_blank">Lesson 2.19</a> and <a href="${linkLesson0221}" target="_blank">Lesson 2.21</a>) is a very important characteristic of rooms. Since too much reverberation in rooms causes low intelligibility, acoustic design often focuses on reducing the sound energy in a room by adding sound absorbing materials on its surfaces. People also contribute to the overall sound absorption in a room since the sound waves travelling through the room also reach them, and their clothes and bodies also absorb a part of the sound energy.<br class=newline>The transmission of sound waves emitted by sound sources in a room through its boundary surfaces such as walls, floors, and ceilings follows a completely different physical mechanism. But, from the perspective of a listener standing in the room, it does not matter how the loss of sound energy in the room occurred, i.e., by transmission through the boundary surfaces or through absorption by them!<br class=newline>Moreover, it is impossible to separate the mechanisms of sound absorption by a surface and sound reflection from it, since they are two parts of the same principle. Therefore, they are explained in the same chapter.<br class=newline>Sound absorption is a physical process of reduction of energy of the sound wave impinging on a surface or simply propagating through an elastic medium such as air. There are two different mechanisms that explain this energy loss: 1. air absorption due to friction between air molecules during propagation, and 2. reflection of sound waves from a surface which is not totally reflective, causing a part of the incident sound energy to be reflected, and a part to be absorbed by the surface. In both cases, the absorbed energy is converted to heat.
<h4 class=subsectionHead>Reflection of a plane wave at normal incidence</h4>
<p>To explain this process, we must start with the complex equation of a plane sound wave. The pressure of the plane wave propagating in the direction x can be written as:
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ωt</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   in Pa is the complex sound pressure at the one-dimensional coordinate
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
   </math>
   in m, and at the time
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>t</mi>
   </math>
   in s.
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mover accent=true>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mo accent=true>^</mo>
      </mover>
   </math>
   in Pa is the magnitude of the wave,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ω</mi>
   </math>
   in Hz is the angular frequency being equal to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>2</mn>
   </math>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>π</mi>
   </math>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
   </math>
   (where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
   </math>
   is the frequency of the wave), and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>k</mi>
   </math>
   is the non-dimensional wave number.<br class=newline>The equation (1) can be used to calculate the sound pressure level for the plane wave in any time instant and at any position along the x axis as the direction of propagation in free field conditions when the wave propagates freely in one-dimensional space without hitting any obstacles. Naturally, this is not realistic, as there are always obstacles to a wave that propagates through a medium, especially in closed rooms bound by their boundary surfaces.<br class=newline>We do know from experience that sound waves reflect from these surfaces. This is quite intuitive, especially if the reflecting surface is far enough from the sound source (around 9 m or more); in this case we hear an echo of the sound emitted by the source. For smaller distances, we become aware of the reflected sound through the perceived coloration of the sound, but we do not hear an echo.<br class=newline>The incident angle of the sound wave can vary between 0<span class=tcrm-1000>° </span>and 90<span class=tcrm-1000>°</span>, but for simplicity, we first assume normal incidence with the reflection angle of 0<span class=tcrm-1000>°</span>. This is shown in <a href="#" data-img-src="${config.ASSETS_URL}/05/03/theory/figure-1.svg" data-img-alt="Figure 1. Reflection of a plane sound wave from a hard surface at normal incidence.">Figure 1</a>. At coordinate x = 0 there is a hard, reflective surface, e.g., a concrete wall. The incident plane wave is propagating towards this surface, and is marked by
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   , and shown by an arrow pointing towards the surface. The incident wave is partly reflected from the wall, and a new, reflected wave is created, as indicated by the arrow pointing away from the surface, marked with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
   </math>
   .
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/05/03/theory/figure-1.svg" alt="Figure 1. Reflection of a plane sound wave from a hard surface at normal incidence."><figcaption class=caption>Figure 1. Reflection of a plane sound wave from a hard surface at normal incidence.</figcaption></figure>
<p>
   We can now define a new parameter that relates to the amount of the incident sound wave being reflected by the wall. At least a small part of the sound wave energy is absorbed by the surface (or transmitted through it to the other side of the wall). This is the so-called <strong><span class=cmbx-10>reflection coefficient</span></strong>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>R</mi>
   </math>
   (sometimes named also reflection factor or reflection index), and it is defined as the ratio of the sound pressure of the reflected and the incident sound wave (2). Since the pressure of both sound waves is essentially complex, the resulting reflection coefficient is also a complex number, represented here by its absolute value
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-rel>|</mo>
      <mi>R</mi>
      <mo class=MathClass-rel>|</mo>
   </math>
   and its phase
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>γ</mi>
   </math>
   .
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>R</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-rel>|</mo>
            <mi>R</mi>
            <mo class=MathClass-rel>|</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jγ</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(2)
</table>
<p>Since at least a small amount of the energy of the incident wave is absorbed by the surface or transmitted through it, the reflected sound wave in equation (2) generally has a smaller amplitude than the incident sound wave. The magnitude of the reflection coefficient has values between 0 and 1, both values included. The sound reflection coefficient equal to 1 means that the sound wave is reflected completely, and the reflected wave is equal to the incident wave. This condition never occurs in reality but can almost be reached if the reflecting surface is (acoustically) very hard, e.g., thick metal or painted concrete. The sound reflection coefficient equal to 0 means that no sound is reflected from the surface, and the incident sound wave is completely absorbed by it. Again, this is unlikely to happen in real life, although very absorptive and thick layers of porous materials such as mineral wool or textile can have such an effect. This is used when building so-called anechoic rooms.<br class=newline>From (1) and (2), the reflected wave can be written as:
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-rel>|</mo>
            <mi>R</mi>
            <mo class=MathClass-rel>|</mo>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ωt</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi>γ</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>R</mi>
   </math>
   is the reflection coefficient, and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>γ</mi>
   </math>
   is the phase change of the reflected wave with reference to the incident wave. This phase change is influenced by the material the reflecting surface is made of. In physical terms it can be quantified by the ratio of the acoustic impedance of air as the propagation medium and the acoustic impedance of the boundary surface as the reflecting medium.<br class=newline>Another quantity related to the behaviour of the reflecting surface when hit by the propagating sound wave is the so-called surface (or wall, or boundary) impedance. It can be calculated as:
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>Z</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>v</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
   </math>
   is the sound pressure at the surface of the reflecting material, and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   in m/s is the particle velocity normal to the wall, generated by the pressure. The particle velocity for non-porous reflecting surfaces equals the velocity of the surface set into motion (vibration) by the incident sound wave. The surface impedance is a complex number, since the particle velocity can be complex as well.<br class=newline>This surface impedance can be understood as a parameter that connects the cause (the pressure of the sound wave) with its effect (the velocity of the surface set into motion because of the existence of the sound wave). Thus, it is the ratio of cause and effect! Moreover, we can calculate the specific (or characteristic) acoustic impedance of a homogeneous propagation medium (such as air) as:
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>ρc</mi>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ρ</mi>
   </math>
   is the density of the medium in kg/
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   , and c is the speed of sound in the medium in m/s. The unit of the acoustic impedance is kg/(
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mi>s</mi>
   </math>
   ), or Rayleigh. For air,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-rel>∼</mo>
   </math>
   415 Rayleigh. The denser the medium, the higher the speed of sound in it. Therefore, specific acoustic impedance of dense materials is much higher than the one found for air. Even for water,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-rel>∼</mo>
   </math>
   1.5
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>⋅</mo>
   </math>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mn>6</mn>
         </mrow>
      </msup>
   </math>
   Rayleigh.<br class=newline>Now we can also define the specific acoustic impedance of a surface as the ratio between the surface impedance and the characteristic impedance of air as:
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(6)
</table>
<p>Finally, to understand the underlying physics of sound reflection, we must also take into account the particle velocity of the incident and reflected sound wave. They can be defined as:
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(7)
</table>
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mi>R</mi>
            <msub>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>v</mi>
   </math>
   is the particle velocity of the incident and reflected wave in m/s, and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>c</mi>
   </math>
   is the characteristic impedance of air.<br class=newline>It is easy to show that the reflection coefficient can now be calculated combining the expressions before, thus we get a new expression that connects the characteristic impedance of the air, and wall impedance of the reflecting surface:
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>R</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>Z</mi>
                  <mo class=MathClass-bin>−</mo>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mi>Z</mi>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Z</mi>
   </math>
   is the wall impedance, and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>c</mi>
   </math>
   the characteristic impedance of the air.<br class=newline>From expression (9), if a reflecting surface is very rigid and dense (which is often the case when sound propagates in rooms), its surface impedance is very high (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Z</mi>
      <mo class=MathClass-rel>→</mo>
      <mi>∞</mi>
   </math>
   ), and the resulting complex reflection coefficient approaches unity. This means that the amplitude of the reflected wave is equal to the amplitude of the incident wave, i.e., all sound energy is reflected. If the surface has a very small surface impedance (near zero) it means that it has an infinitely high ability to move as a response to the changing sound pressure of the incident sound wave. In this case, the resulting reflection coefficient is equal to -1. Such a “soft” reflecting surface again reflects the incident sound wave completely, but the reflected wave has its phase inverted, i.e., the phase change is 180<span class=tcrm-1000>°</span>. Although this example is not relevant in room acoustics, such an effect occurs if the sound wave propagates through a dense medium and hits a reflecting surface of much lower density. For example, sound can propagate in water, hit the border between water and air, and be completely reflected from it back into water. Finally, if the surface impedance is equal to the specific impedance of air
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>c</mi>
   </math>
   , the reflection coefficient is equal to zero. In this case, there is no reflected sound wave, and all sound energy is absorbed by the surface.
<h4 class=subsectionHead>Reflection of a plane wave at oblique incidence</h4>
<p>
   A more general case to consider is the reflection of sound waves from a flat surface with the angle of incidence ranging between 0<span class=tcrm-1000>° </span>(normal incidence) and 90<span class=tcrm-1000>° </span>(grazing incidence), as shown in <a href="#" data-img-src="${config.ASSETS_URL}/05/03/theory/figure-2.svg" data-img-alt="Figure 2. Reflection of a plane sound wave from a hard surface at oblique incidence.">Figure 2</a>. The angle
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>𝜃</mi>
   </math>
   indicates the deviation from normal incidence. It is important to notice that the angle of incidence is equal to the angle of reflection, given that the reflecting surface is flat, even and smooth.<br class=newline>The expressions for the complex sound pressure level of the incident and reflected wave from the previous chapter now change to account for angle
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>𝜃</mi>
   </math>
   , yielding:
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>y</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ωt</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>ky</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(10)
</table>
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>y</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <mi>R</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ωt</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>ky</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>R</mi>
   </math>
   is the complex reflection coefficient.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/05/03/theory/figure-2.svg" alt="Figure 2. Reflection of a plane sound wave from a hard surface at oblique incidence."><figcaption class=caption>Figure 2. Reflection of a plane sound wave from a hard surface at oblique incidence.</figcaption></figure>
<p>By introducing the generalized angle of incidence, expression (9) for calculating the reflection coefficient is now changed to:
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>R</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>Z</mi>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-bin>−</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>Z</mi>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(12)
</table>
<p>
   According to expression (12), one can see that with the increase of the incidence angle
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>𝜃</mi>
   </math>
   from 0<span class=tcrm-1000>° </span>towards 90<span class=tcrm-1000>°</span>, the reflection coefficient starts to gravitate towards value -1 regardless of the impedances. This means that the sound wave has less and less interaction with the surface until it propagates along it at reflection angle 90<span class=tcrm-1000>°</span>.
<h4 class=subsectionHead>Sound absorption</h4>
<p>
   Sound absorption is a physical effect showing how much sound energy is being absorbed by a surface hit by a sound wave. Thus, it is a parameter that contrasts sound reflection from a hard surface. Sound absorption is characterized by the
   <strong>
      <span class=cmbx-10>sound absorption </span><span class=cmbx-10>coefficient, </span>
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>α</mi>
      </math>
   </strong>
   which is defined as the ratio of energy not being reflected from a surface to the total energy of the incident wave. Since the reflection coefficient is indicating the pressure ratio of the reflected to the incident sound waves, it is obvious that the sound absorption and reflection coefficients are complementary in the basic idea. It must be taken into account that they do not relate to the same physical parameter (energy for absorption vs. pressure for reflection). Sound intensity (as an energy parameter) is related to the squared value of sound pressure, hence the absorption coefficient is related to the squared value of the reflection coefficient, or:
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>α</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>−</mo>
            <mo class=MathClass-rel>|</mo>
            <mi>R</mi>
            <msup>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(13)
</table>
<p>
   By observing closely the expressions for calculating both the reflection and absorption coefficient (12), (13), we can conclude that almost all energy is reflected if a sound wave hits a hard surface (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>R</mi>
      <mo class=MathClass-rel>≈</mo>
      <mn>1</mn>
   </math>
   ,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>α</mi>
      <mo class=MathClass-rel>≈</mo>
      <mn>0</mn>
   </math>
   ). If a surface is made of a very absorptive material with acoustic impedance similar to the characteristic impedance of air (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Z</mi>
      <mo class=MathClass-rel>≈</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   ), almost all energy of the incident wave is absorbed by the surface (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>R</mi>
      <mo class=MathClass-rel>≈</mo>
      <mn>0</mn>
   </math>
   ,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>α</mi>
      <mo class=MathClass-rel>≈</mo>
      <mn>1</mn>
   </math>
   ). Real life cases are always somewhere in between these two extremes.<br class=newline>Generally speaking, sound absorption and reflection are frequency-dependent phenomena, in the sense that the amount of sound energy absorbed by materials and constructions depends on frequency within the entire frequency range of interest (audible range is from 20 Hz to 20 kHz). Absorbers as elements of the acoustical design of rooms are described in detail in <a href="${linkLesson0508}" target="_blank">Lesson 5.8</a>. Absorption coefficient as a function of frequency is shown in <a href="#" data-img-src="${config.ASSETS_URL}/05/03/theory/figure-3.svg" data-img-alt="Figure 3. Sound absorption coefficient for some common materials for the frequency range 125 Hz to 4000 Hz.">Figure 3</a> for common materials and constructions.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/05/03/theory/figure-3.svg" alt="Figure 3. Sound absorption coefficient for some common materials for the frequency range 125 Hz to 4000 Hz."><figcaption class=caption>Figure 3. Sound absorption coefficient for some common materials for the frequency range 125 Hz to 4000 Hz.</figcaption></figure>
<p>The decrease of the sound pressure magnitude of a sound wave does not occur only due to sound absorption as the wave hits a reflecting/absorbing surface. The magnitude also decreases because of sound attenuation in air due to viscosity, heat conduction and thermal relaxation of the medium. This effect is observable only at high frequencies and for long-distance propagation outdoors, but also in large rooms when it becomes obvious that sound attenuates faster than can be explained only by the absorption coefficient of the room surfaces.
<h4 class=subsectionHead>Reflection of spherical sound waves</h4>
<p>
   It is important to mention that not all sound waves are plane. On the contrary, a sound source is usually approximated with a point source, especially at close range, meaning that the sound intensity decreases with the distance from the source with the factor of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <msup>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   , and the sound pressure decreases with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <mi>r</mi>
   </math>
   , where r is the distance between the source and the sound intensity measurement point. Such sound waves are represented as spherical sound waves, and the expressions for sound reflection derived for plane waves become more complex, as shown in <a href="#" data-img-src="${config.ASSETS_URL}/05/03/theory/figure-4.svg" data-img-alt="Figure 4. Reflection of a spherical wave from a plane surface.">Figure 4</a>. If the sound source is sufficiently far from the reflecting surface, at the moment of reflection the sound pressure can be calculated locally as for a plane wave.<br class=newline>There is another interesting fact that can be concluded from <a href="#" data-img-src="${config.ASSETS_URL}/05/03/theory/figure-4.svg" data-img-alt="Figure 4. Reflection of a spherical wave from a plane surface.">Figure 4</a>. The sound wave reflected from a flat surface appears as if it was being emitted by an image point source on the other side of the reflecting surface. This is the so-called image source model which is often used for calculating the sound field distribution in a room due to numerous reflections from the boundary surfaces.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/05/03/theory/figure-4.svg" alt="Figure 4. Reflection of a spherical wave from a plane surface."><figcaption class=caption>Figure 4. Reflection of a spherical wave from a plane surface.</figcaption></figure>
<h4 class=subsectionHead>Reflection of plane waves from curved surfaces</h4>
<p>
   Not all reflecting surfaces are flat! Even if the surface itself is smooth, concave and convex surfaces are often used in architecture for reasons of building statics (e.g. arches, vaults, domes), or just for aesthetics. Such surfaces can scatter or focus the sound energy, as shown in <a href="#" data-img-src="${config.ASSETS_URL}/05/03/theory/figure-5.svg" data-img-alt="Figure 5. Reflection of a plane wave from a convex and a concave surface.">Figure 5</a>. This fact is often used to optimize the shape of these reflecting surfaces to be used as reflectors of sound waves in rooms in order to achieve a harmonized distribution of the sound pressure level over a certain area, for example the audience area in concert halls.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/05/03/theory/figure-5.svg" alt="Figure 5. Reflection of a plane wave from a convex and a concave surface."><figcaption class=caption>Figure 5. Reflection of a plane wave from a convex and a concave surface.</figcaption></figure>
<h3 class=sectionHead>Sound scattering</h3>
<p>
   Until now, we assumed that the reflecting surfaces are smooth and even, which is not always the case. Each room surface has some roughness, depending on the scale of observation. Even smooth metal plates show roughness and unevenness if observed under a microscope. So, when does the roughness of the surface influence the behaviour of the reflections? It depends on the wavelength of the sound – the sound wave starts to interact with the unevenness of the surfaces when the wavelength starts to be similar in size with the dents and creases.<br class=newline>The strength of sound wave scattering due to the roughness of the surface is shown in <a href="#" data-img-src="${config.ASSETS_URL}/05/03/theory/figure-6.svg" data-img-alt="Figure 6. Sound reflection and scattering, depending on the relation between the roughness of the surface and the wavelength of the wave.">Figure 6</a>.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/05/03/theory/figure-6.svg" alt="Figure 6. Sound reflection and scattering, depending on the relation between the roughness of the surface and the wavelength of the wave."><figcaption class=caption>Figure 6. Sound reflection and scattering, depending on the relation between the roughness of the surface and the wavelength of the wave.</figcaption></figure>
<p>
   The amount of the sound energy that is scattered from a surface in all other directions except the specular one can be described by the <strong><span class=cmbx-10>sound scattering coefficient</span></strong>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>s</mi>
   </math>
   . It is defined as the ratio of the scattered energy compared to the total reflected energy. The reflected energy is somewhat smaller than the total incident sound energy since a part of it is always absorbed by the surface. This is visualized in <a href="#" data-img-src="${config.ASSETS_URL}/05/03/theory/figure-7.svg" data-img-alt="Figure 7. Sound wave scattering from a rough surface.">Figure 7</a>.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/05/03/theory/figure-7.svg" alt="Figure 7. Sound wave scattering from a rough surface."><figcaption class=caption>Figure 7. Sound wave scattering from a rough surface.</figcaption></figure>
<p>
   If we normalize the incident sound energy to 1, the total reflected energy is equal to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-open>(</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>−</mo>
      <mi>α</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   . This energy can be divided into the scattered part of the energy equalling to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-open>(</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>−</mo>
      <mi>α</mi>
      <mo class=MathClass-close>)</mo>
      <mi>s</mi>
   </math>
   , and the energy that is reflected specularly, equalling to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-open>(</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>−</mo>
      <mi>α</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-open>(</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>−</mo>
      <mi>s</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   in order to keep the energy preservation law. Taking all of this into account, we can write the expression for calculating the scattering coefficient as:
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>s</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>E</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>scatt</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>E</mi>
                     </mrow>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>E</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>spec</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>E</mi>
                     </mrow>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>a</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi>α</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mi>α</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(14)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>a</mi>
   </math>
   is the specular absorption coefficient. It can be considered as an apparent absorption coefficient of the surface since the sound energy is scattered instead of absorbed, it takes into account both the absorption and the scattering coefficient.<br class=newline>Sound scattering on reflecting surfaces increases the sound diffusion in the room and it helps the sound energy to become more evenly diffused in closed spaces. This is often desirable in the acoustic design of rooms since sound diffusion prevents strong reflections or focusing of sound energy, helping in a uniform distribution of sound energy in a room in order to strengthen the diffuse sound field.<br class=newline>Many architectural elements are used to increase the sound diffusion in the room by introducing structures that have an optimum scattering behaviour, depending on the desired frequency range. Such structures are all convex surfaces, columns, audience seating area, other irregular or periodic geometric structures, alternating absorbing and reflecting structures, but also predesigned diffusers using mathematical models. Diffusers as elements of the acoustical design of rooms are described in detail in <a href="${linkLesson0509}" target="_blank">Lesson 5.9</a>. An example of the frequency-dependent scattering coefficients for empty and occupied chairs and tables are shown in <a href="#" data-img-src="${config.ASSETS_URL}/05/03/theory/figure-8.svg" data-img-alt="Figure 8. Sound scattering coefficient for chairs and tables.">Figure 8</a>.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/05/03/theory/figure-8.svg" alt="Figure 8. Sound scattering coefficient for chairs and tables."><figcaption class=caption>Figure 8. Sound scattering coefficient for chairs and tables.</figcaption></figure>
<h3 class=sectionHead>Sound diffraction</h3>
<p>Until now, we considered sound reflection and scattering from surfaces of undetermined size. For simplicity, the assumption was that all surfaces are infinitely large. Consequently, there was no need to think about the interaction of the sound wave and the (sharp) edges of surfaces and 3D bodies. This interaction is, in fact, very important, as it causes the diffraction effect.<br class=newline>Diffraction of sound waves occurs not only on edges of bodies and surfaces, but also on any kind of boundary where materials with different acoustic impedance meet (e.g., objects built from different materials). This shows that diffraction is a very common effect in open spaces, particularly when considering the efficiency of screens as noise barriers, but it is also present in closed rooms. The diffraction effect causes the sound wave to be scattered at the interaction edge. This phenomenon makes it difficult to precisely calculate the sound field in a room. Diffraction is also the main reason why room acoustics simulation software relies mainly on statistical sound distribution, as it would be computationally intense and time-consuming to use the wave equation and numerical methods, especially at higher frequencies.<br class=newline>Diffraction occurs in an observable and measurable manner if the size of the object / protrusion causing the diffraction is not too small compared to the wavelength of the sound wave, similarly to sound scattering from a rough surface.<br class=newline>The effect of sound diffraction can be observed if a thin screen (barrier) is placed in the path of a sound wave propagating from a sound source into surrounding space. Although there is no line-of-sight between the sound source and a sound receiver placed behind the screen, the measured sound pressure level at the receiver position is lower than it would be if there was no screen, but it is certainly not zero. It can be said that the sound wave is bent around the diffraction edge of the screen. The so-called shadow region behind the screen is not sound-free. It is exposed to sound waves, but their intensity is lower compared to the case with no screen at all.<br class=newline>There is a simple way to calculate the amount of sound energy that gets behind such screens, barriers, and objects due to diffraction. For this reason, we usually calculate the <strong><span class=cmbx-10>insertion loss</span></strong>, which is the decrease of sound pressure level at a certain position due to the existence of the screen, compared to the sound pressure level at the same position without the screen. This situation is shown in <a href="#" data-img-src="${config.ASSETS_URL}/05/03/theory/figure-9.svg" data-img-alt="Figure 9. Insertion loss calculation due to sound diffraction around the screen edge.">Figure 9</a>. The insertion loss is calculated using the following expression:
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi mathvariant=italic>ΔL</mi>
            <mo class=MathClass-rel>≈</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mn>3</mn>
            <mo class=MathClass-bin>+</mo>
            <mn>20</mn>
            <mfrac>
               <mrow>
                  <mn>2</mn>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mi>λ</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(15)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>d</mi>
   </math>
   is the difference between the diffracted path and the direct path from sound to receiver of the sound wave in m,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>d</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>a</mi>
      <mo class=MathClass-bin>+</mo>
      <mi mathvariant=italic>b–c</mi>
   </math>
   ,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>λ</mi>
   </math>
   is the wavelength of the sound wave in m, and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>ΔL</mi>
   </math>
   is the insertion loss in dB.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/05/03/theory/figure-9.svg" alt="Figure 9. Insertion loss calculation due to sound diffraction around the screen edge."><figcaption class=caption>Figure 9. Insertion loss calculation due to sound diffraction around the screen edge.</figcaption></figure>
<p>
   From expression (15) it is obvious that the insertion loss increases as the path difference between the diffracted (detour) path and the direct path between the sound source and the receiver increases. At the same time, the effect is frequency-dependent, as the insertion loss increases with decreasing wavelength
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>λ</mi>
   </math>
   , i.e. with increasing frequency, so the barriers are less effective at low frequencies, as stated before and shown in <a href="#" data-img-src="${config.ASSETS_URL}/05/03/theory/figure-9.svg" data-img-alt="Figure 9. Insertion loss calculation due to sound diffraction around the screen edge.">Figure 9</a>.<br class=newline>The diffraction of the sound wave occurs not only on edges, but also on openings and obstacles, as shown in <a href="#" data-img-src="${config.ASSETS_URL}/05/03/theory/figure-10.svg" data-img-alt="Figure 10. Insertion loss calculation due to sound diffraction around the screen edge [3].">Figure 10</a>. The same rule applies – the intensity of the sound in the shadow behind the surface with the opening or behind an obstacle depends on the ratio of the dimension of the opening/obstacle shown with a blue line, and the wavelength of sound shown with a red line.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/05/03/theory/figure-10.svg" alt="Figure 10. Insertion loss calculation due to sound diffraction around the screen edge [3]."><figcaption class=caption>Figure 10. Insertion loss calculation due to sound diffraction around the screen edge [3].</figcaption></figure>
<p>Diffraction is very intuitive to us, as we can hear sound even if it is coming, for example, from behind a corner of a building. It is an important sound propagation effect in many real-life scenarios. Orchestral music diffracts around the edge of the orchestra pit in opera houses. Sound diffracts around the peaks and dips of the outer ear, thus producing a unique set of Head-Related Transfer Functions for each person (see <a href="${linkLesson0215}" target="_blank">Lesson 2.15</a>). Noise barriers built along busy roads become less effective as a noise-reduction measure due to diffraction.
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-comm>[1]
   <dd class=thebibliography id=bib-1>
      <p>edX Course. <span class=cmti-10>Fundamentals of Communication Acoustics</span>. https://courses.edx.org/dashboard, 2019.
   <dt class=thebibliography id=X0-room>[2]
   <dd class=thebibliography id=bib-2>
      <p>Heinrich Kuttruff. <span class=cmti-10>Room Acoustics</span>. 5. ed. New York: Spon Press, 2009. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780367870997.
   <dt class=thebibliography id=X0-multi>[3]
   <dd class=thebibliography id=bib-3>
      <p><span class=cmti-10>Multibook of Architectural Acoustics</span>. https://arac-multibook.com, 2018.
   <dt class=thebibliography id=X0-aura>[4]
   <dd class=thebibliography id=bib-4>
      <p>Michael Vorländer. <span class=cmti-10>Auralization</span>. 1. ed. Berlin: Springer, 2008. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9783642080234.
</dl>`;
export default theory;
